import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, IconButton, Typography, Slider, styled, useTheme, Avatar } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward30Icon from '@mui/icons-material/Forward30';
import { usePlayer } from '../../providers/player-provider';
import { stripHtmlTags } from '../../utils/html-utils';

const PlayerCard = styled(Card)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.drawer + 2, // Above drawer
    borderRadius: '16px 16px 0 0',
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0px -4px 20px rgba(0,0,0,0.1)',
    backdropFilter: 'blur(10px)',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    borderTop: `1px solid ${theme.palette.divider}`,
}));

export default function GlobalPlayer() {
    const { currentEpisode, isPlaying, togglePlay, closePlayer, setIsPlaying } = usePlayer();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const theme = useTheme();

    useEffect(() => {
        if (currentEpisode && audioRef.current) {
           if(isPlaying){
                audioRef.current.play().catch(e => console.error("Playback failed", e));
           } else {
                audioRef.current.pause();
           }
        }
    }, [currentEpisode, isPlaying]);

    const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
        setCurrentTime(e.currentTarget.currentTime);
    };

    const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
        setDuration(e.currentTarget.duration);
    };

    const handleEnded = () => {
        setIsPlaying(false);
    };

    const handleSkipBackward = () => {
        if (audioRef.current) {
            const newTime = audioRef.current.currentTime - 15;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const handleSkipForward = () => {
        if (audioRef.current) {
            const newTime = audioRef.current.currentTime + 30;
            audioRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    const handleSeek = (_: Event, newValue: number | number[]) => {
        if (audioRef.current) {
            audioRef.current.currentTime = newValue as number;
            setCurrentTime(newValue as number);
        }
    };


    const formatTime = (time: number) => {
        if (!time || isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    if (!currentEpisode) return null;

    return (
        <PlayerCard>
            <audio
                ref={audioRef}
                src={currentEpisode.enclosureUrl}
                autoPlay={isPlaying}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
            />

            {/* Episode Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 200, maxWidth: 400 }}>
                 <Avatar 
                    variant="rounded" 
                    src={currentEpisode.imageUrl} 
                    alt="cover"
                    sx={{ width: 48, height: 48 }}
                />
                <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="subtitle2" noWrap fontWeight="bold">
                        {currentEpisode.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {stripHtmlTags(currentEpisode.description || '')}
                    </Typography>
                </Box>
            </Box>

            {/* Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <IconButton size="small" onClick={handleSkipBackward}>
                     <Replay10Icon fontSize="small" />
                 </IconButton>
                 
                 <IconButton 
                    onClick={togglePlay} 
                    sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' } 
                    }}
                >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>

                <IconButton size="small" onClick={handleSkipForward}>
                     <Forward30Icon fontSize="small" />
                 </IconButton>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                    {formatTime(currentTime)}
                </Typography>
                <Slider
                    size="small"
                    value={currentTime}
                    max={duration || 100}
                    onChange={handleSeek}
                    sx={{ color: theme.palette.mode === 'dark' ? '#fff' : 'primary.main' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                    {formatTime(duration)}
                </Typography>
            </Box>

            {/* Close Button */}
            <IconButton onClick={closePlayer} size="small">
                <CloseIcon />
            </IconButton>
        </PlayerCard>
    );
}
